// Every read and write the desk does. Reads go straight to tables and the
// security_invoker views, so RLS decides what comes back; writes go through
// the RPCs in supabase/migrations/202610200002_core_desk.sql, which check
// permissions themselves and write the lead history.

import { useEffect } from 'react';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Market, Stage } from '@ouradent/shared';
import { supabase } from './supabase';
import type { BoardLead, Call, Client, InboxRow, Profile, Task, TimelineEvent } from './types';

export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: true, retry: 1 } },
});

async function unwrap<T>(p: PromiseLike<{ data: T | null; error: { message: string } | null }>) {
  const { data, error } = await p;
  if (error) throw new Error(error.message);
  return data as T;
}

// Reads ---------------------------------------------------------------------

export const useTeam = () =>
  useQuery({
    queryKey: ['team'],
    queryFn: () =>
      unwrap<Profile[]>(
        supabase
          .from('profiles')
          .select('id, full_name, email, role, external, avatar_bg, avatar_fg')
          .eq('active', true)
          .order('full_name'),
      ),
    staleTime: 5 * 60_000,
  });

export const useBoard = (enabled = true) =>
  useQuery({
    queryKey: ['board'],
    queryFn: () => unwrap<BoardLead[]>(supabase.from('lead_board').select('*')),
    enabled,
  });

export const useTasks = (enabled = true) =>
  useQuery({
    queryKey: ['tasks'],
    queryFn: () =>
      unwrap<Task[]>(
        supabase
          .from('tasks')
          .select(
            'id, lead_id, call_id, text, owner_id, co_owner_id, due_at, done_at, origin, client_id, priority, stage, notes, created_by',
          )
          .order('due_at'),
      ),
    enabled,
  });

export const useCalls = (enabled = true) =>
  useQuery({
    queryKey: ['calls'],
    queryFn: () =>
      unwrap<Call[]>(
        supabase
          .from('calls')
          .select(
            'id, lead_id, title, started_at, duration_min, status, attendee, summary, recap_points, objections, promises, next_step, dm, suggested_stage, extracted_at, extraction_model, report_url, processed_by, processed_at',
          )
          .order('started_at', { ascending: false }),
      ),
    enabled,
  });

export const useInbox = () =>
  useQuery({
    queryKey: ['inbox'],
    queryFn: () => unwrap<InboxRow[]>(supabase.from('action_inbox').select('*').order('priority')),
  });

export const useTimeline = (leadId: string | null) =>
  useQuery({
    queryKey: ['timeline', leadId],
    queryFn: () =>
      unwrap<TimelineEvent[]>(
        supabase
          .from('timeline_events')
          .select('id, lead_id, kind, text, actor_id, at')
          .eq('lead_id', leadId!)
          .order('at', { ascending: false }),
      ),
    enabled: Boolean(leadId),
  });

export const searchLeads = (q: string) =>
  unwrap<BoardLead[]>(supabase.rpc('search_leads', { p_q: q }));

// Writes --------------------------------------------------------------------

const LEAD_KEYS = [['board'], ['tasks'], ['inbox'], ['timeline'], ['calls']];

function useRpc<A>(fn: (args: A) => PromiseLike<{ error: { message: string } | null }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: A) => {
      const { error } = await fn(args);
      if (error) throw new Error(error.message);
    },
    onSettled: () => {
      for (const key of LEAD_KEYS) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export type NewLead = {
  person: string;
  clinic: string;
  market: Market;
  email?: string;
  phone?: string;
  city?: string;
  source: string;
};

export async function createLead(l: NewLead): Promise<{ id: string; existed: boolean }> {
  const data = await unwrap<{ id: string; existed: boolean }>(
    supabase.rpc('create_lead', {
      p_person: l.person,
      p_clinic: l.clinic,
      p_market: l.market,
      p_email: l.email || null,
      p_phone: l.phone || null,
      p_city: l.city || null,
      p_source: l.source,
    }),
  );
  for (const key of LEAD_KEYS) void queryClient.invalidateQueries({ queryKey: key });
  return data;
}

export const useMoveStage = () =>
  useRpc((a: { lead: string; stage: Stage; reason?: string | null; returnAt?: string | null }) =>
    supabase.rpc('move_stage', {
      p_lead: a.lead,
      p_stage: a.stage,
      p_reason: a.reason ?? null,
      p_return_at: a.returnAt ?? null,
    }),
  );

export const useSetOwner = () =>
  useRpc((a: { lead: string; owner: string }) =>
    supabase.rpc('set_lead_owner', { p_lead: a.lead, p_owner: a.owner }),
  );

export const useSetNextStep = () =>
  useRpc((a: { lead: string; text: string; due: string; owner: string; complete: boolean }) =>
    supabase.rpc('set_next_step', {
      p_lead: a.lead,
      p_text: a.text,
      p_due: a.due,
      p_owner: a.owner,
      p_complete: a.complete,
    }),
  );

export const useAddTask = () =>
  useRpc((a: { lead: string | null; text: string; owner: string; due: string }) =>
    supabase.rpc('add_task', { p_lead: a.lead, p_text: a.text, p_owner: a.owner, p_due: a.due }),
  );

export const useSetTaskDone = () =>
  useRpc((a: { task: string; done: boolean }) =>
    supabase.rpc('set_task_done', { p_task: a.task, p_done: a.done }),
  );

// Realtime ------------------------------------------------------------------
// Anything a teammate changes shows up without a reload. Realtime applies the
// same RLS, so nobody receives rows they could not select.

const WATCHED = {
  leads: [['board'], ['inbox'], ['overview']],
  next_steps: [['board'], ['inbox']],
  tasks: [['tasks'], ['inbox'], ['board']],
  calls: [['calls'], ['inbox'], ['board'], ['overview']],
  timeline_events: [['timeline']],
  calendar_events: [['calendar']],
  clients: [['clients']],
  weekly_priorities: [['priorities']],
  notifications: [['notifications']],
  wa_conversations: [['wa']],
  messages: [['wa']],
} as const;

export function useRealtime(enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    const channel = supabase.channel('desk');
    for (const [table, keys] of Object.entries(WATCHED)) {
      channel.on('postgres_changes', { event: '*', schema: 'public', table }, () => {
        for (const key of keys) void qc.invalidateQueries({ queryKey: [...key] });
      });
    }
    channel.subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [enabled, qc]);
}

// AI draft of a call review from pasted Read.ai text (Edge Function
// extract-call). Returns a sentence for the reviewer when it cannot run.
const EXTRACT_ERRORS: Record<string, string> = {
  ai_not_configured: 'AI extraction is not switched on yet. Fill in the review by hand.',
  ai_key_invalid:
    'The AI key is not valid. Ask an owner to check it, and fill in the review by hand.',
  ai_busy: 'The AI is busy right now. Try again in a minute.',
  ai_refusal: 'The AI declined this text. Fill in the review by hand.',
  ai_truncated:
    'The text is too long for one go. Paste the summary instead of the full transcript.',
  already_confirmed: 'This call is already confirmed.',
  not_found: 'You cannot see this call.',
};

export async function extractCall(callId: string, source: string) {
  const { data, error } = await supabase.functions.invoke<{
    promises: number;
    droppedWithoutEvidence: number;
  }>('extract-call', { body: { callId, source } });
  if (error) {
    let code = '';
    const ctx = (error as { context?: unknown }).context;
    if (ctx instanceof Response) {
      code = ((await ctx.json().catch(() => ({}))) as { error?: string }).error ?? '';
    }
    throw new Error(EXTRACT_ERRORS[code] ?? 'The AI draft failed. Fill in the review by hand.');
  }
  await queryClient.invalidateQueries({ queryKey: ['calls'] });
  return data!;
}

// Calendar (docs/specs/calendar.md) ---------------------------------------------

export type CalEvent = {
  id: string;
  google_id: string;
  status: string;
  summary: string | null;
  description: string | null;
  location: string | null;
  start_at: string;
  end_at: string;
  all_day: boolean;
  attendees: { email: string; name: string | null; response: string | null }[];
  html_link: string | null;
  meet_link: string | null;
  lead_id: string | null;
};

const CAL_COLUMNS =
  'id, google_id, status, summary, description, location, start_at, end_at, all_day, attendees, html_link, meet_link, lead_id';

/** Events overlapping [from, to). */
export const useCalendar = (from: string, to: string, enabled = true) =>
  useQuery({
    queryKey: ['calendar', from, to],
    queryFn: () =>
      unwrap<CalEvent[]>(
        supabase
          .from('calendar_events')
          .select(CAL_COLUMNS)
          .lt('start_at', to)
          .gt('end_at', from)
          .order('start_at'),
      ),
    enabled,
  });

export const useLeadEvents = (leadId: string | null) =>
  useQuery({
    queryKey: ['calendar', 'lead', leadId],
    queryFn: () =>
      unwrap<CalEvent[]>(
        supabase
          .from('calendar_events')
          .select(CAL_COLUMNS)
          .eq('lead_id', leadId!)
          .order('start_at'),
      ),
    enabled: Boolean(leadId),
  });

export type CalConnection = {
  account_email: string;
  connected_at: string;
  last_synced_at: string | null;
  gmail: boolean;
} | null;

export const useCalendarConnection = (enabled = true) =>
  useQuery({
    queryKey: ['calendar', 'connection'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('calendar_connection');
      if (error) throw new Error(error.message);
      return (data ?? null) as CalConnection;
    },
    enabled,
  });

const CAL_ERRORS: Record<string, string> = {
  not_connected: 'The Google calendar is not connected yet.',
  google_reconnect: 'Google no longer accepts the connection. An owner has to reconnect it.',
  end_before_start: 'The end has to be after the start.',
  forbidden: 'You do not have access to the calendar.',
};

export async function calendarAction<T = Record<string, unknown>>(
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>('calendar', { body });
  if (error) {
    let code = '';
    let details = '';
    const ctx = (error as { context?: unknown }).context;
    if (ctx instanceof Response) {
      const j = (await ctx.json().catch(() => ({}))) as { error?: string; details?: string };
      code = j.error ?? '';
      details = j.details ?? '';
    }
    throw new Error(CAL_ERRORS[code] ?? (details || 'Google Calendar did not accept the change.'));
  }
  await queryClient.invalidateQueries({ queryKey: ['calendar'] });
  return data as T;
}

const MAIL_ERRORS: Record<string, string> = {
  no_email: 'This lead has no email address.',
  unsubscribed: 'This lead unsubscribed. Nothing can be sent to them.',
  blacklisted: 'This address is on the do-not-contact list.',
  not_connected: 'Google is not connected yet. An owner connects it on Calendar.',
  google_reconnect: 'Gmail is not allowed yet. An owner reconnects Google on Calendar.',
};

/** Sends one email to a lead from ahoy@ouradent.com (Gmail). */
export async function sendLeadEmail(leadId: string, subject: string, body: string) {
  const { error } = await supabase.functions.invoke('gmail-send', {
    body: { leadId, subject, body },
  });
  if (error) {
    let code = '';
    let details = '';
    const ctx = (error as { context?: unknown }).context;
    if (ctx instanceof Response) {
      const j = (await ctx.json().catch(() => ({}))) as { error?: string; details?: string };
      code = j.error ?? '';
      details = j.details ?? '';
    }
    throw new Error(MAIL_ERRORS[code] ?? (details || 'The email was not sent.'));
  }
  await queryClient.invalidateQueries({ queryKey: ['timeline'] });
}

// Clients and tasks (docs/specs/tasks.md) ----------------------------------------

export const useClients = () =>
  useQuery({
    queryKey: ['clients'],
    queryFn: () =>
      unwrap<Client[]>(
        supabase.from('clients').select('id, name, colour, created_by').order('name'),
      ),
  });

/** Members of the clients I created (the only memberships I may read besides my own). */
export const useClientMembers = () =>
  useQuery({
    queryKey: ['clients', 'members'],
    queryFn: () =>
      unwrap<{ client_id: string; user_id: string }[]>(
        supabase.from('client_members').select('client_id, user_id'),
      ),
  });

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new Error(error.message);
  return data as T;
}

export async function saveTask(id: string | null, fields: Record<string, unknown>) {
  const r = await rpc<string>('save_task', { p_id: id, p: fields });
  await queryClient.invalidateQueries({ queryKey: ['tasks'] });
  await queryClient.invalidateQueries({ queryKey: ['inbox'] });
  return r;
}

export async function deleteTask(id: string) {
  await rpc('delete_task', { p_id: id });
  await queryClient.invalidateQueries({ queryKey: ['tasks'] });
  await queryClient.invalidateQueries({ queryKey: ['inbox'] });
}

export async function saveClient(
  id: string | null,
  name: string,
  colour: string,
  members: string[],
) {
  const r = await rpc<string>('save_client', {
    p_id: id,
    p_name: name,
    p_colour: colour,
    p_members: members,
  });
  await queryClient.invalidateQueries({ queryKey: ['clients'] });
  return r;
}

export async function deleteClient(id: string) {
  await rpc('delete_client', { p_id: id });
  await queryClient.invalidateQueries({ queryKey: ['clients'] });
  await queryClient.invalidateQueries({ queryKey: ['tasks'] });
}

// CEO overview (docs/specs/overview.md) -----------------------------------------

export type OverviewMetrics = {
  leads_week: number;
  leads_prev: number;
  sources_week: Record<string, number>;
  booked_week: number;
  booked_prev: number;
  noshow_week: number;
  calls_week: number;
  calls_prev: number;
  offers_week: number;
  offers_prev: number;
  won_month: number;
  won_prev: number;
  funnel: { leads: number; call: number; offer: number; won: number };
  stuck: { id: string; clinic: string; stage: Stage; days: number }[];
};

export const useOverview = (enabled = true) =>
  useQuery({
    queryKey: ['overview'],
    queryFn: () => rpc<OverviewMetrics>('overview_metrics', {}),
    enabled,
    refetchInterval: 5 * 60_000,
  });

export type AdSpendWeek = {
  spend: number;
  campaigns: {
    name: string;
    spend: number;
    leads: number;
    meta_leads: number;
    cpl: number | null;
  }[];
};

export const useAdSpend = (enabled = true) =>
  useQuery({
    queryKey: ['overview', 'spend'],
    queryFn: () => rpc<AdSpendWeek>('ad_spend_week', {}),
    enabled,
  });

export type WeeklyPriority = {
  id: string;
  week_start: string;
  rank: number;
  title: string;
  status: 'todo' | 'doing' | 'done' | 'dropped';
  note: string | null;
};

/** Monday of the week containing `d`, as YYYY-MM-DD in local time. */
export function mondayOf(d = new Date()): string {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  const p = (n: number) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`;
}

/** My priorities for this week and the one before (RLS returns only my rows). */
export const usePriorities = (week: string, prev: string) =>
  useQuery({
    queryKey: ['priorities', week],
    queryFn: () =>
      unwrap<WeeklyPriority[]>(
        supabase
          .from('weekly_priorities')
          .select('id, week_start, rank, title, status, note')
          .in('week_start', [week, prev])
          .order('rank'),
      ),
  });

/** Updates priority `id`, or creates slot `rank` of `week` when `id` is null. */
export async function savePriority(
  id: string | null,
  week: string,
  rank: number,
  fields: Partial<Pick<WeeklyPriority, 'title' | 'status' | 'note'>>,
) {
  const { error } = id
    ? await supabase.from('weekly_priorities').update(fields).eq('id', id)
    : await supabase.from('weekly_priorities').insert({ week_start: week, rank, ...fields });
  if (error) throw new Error(error.message);
  await queryClient.invalidateQueries({ queryKey: ['priorities'] });
}

export async function clearPriority(id: string) {
  const { error } = await supabase.from('weekly_priorities').delete().eq('id', id);
  if (error) throw new Error(error.message);
  await queryClient.invalidateQueries({ queryKey: ['priorities'] });
}

// Notifications (docs/specs/push.md) --------------------------------------------

export type Notice = {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  url: string | null;
  created_at: string;
  read_at: string | null;
};

export const NOTICE_KINDS: { id: string; label: string; hint: string }[] = [
  { id: 'new_lead', label: 'New leads', hint: 'Yours, or every new lead nobody has yet' },
  { id: 'call_review', label: 'Calls to review', hint: 'A Read.ai call is ready' },
  { id: 'reply', label: 'Lead replies', hint: 'WhatsApp or email in GoHighLevel' },
  { id: 'automation', label: 'Automation reminders', hint: 'Leads waiting for a first contact' },
  { id: 'meeting_soon', label: 'Meetings', hint: '15 minutes before' },
  { id: 'followup', label: 'Call follow-ups', hint: 'Cold callers: a follow-up is due' },
  { id: 'digest', label: 'Morning summary', hint: '7:45, meetings, tasks and priorities' },
  { id: 'no_step', label: 'Leads without a next step', hint: '9:00, if any' },
  { id: 'weekly', label: 'Top 3 of the week', hint: 'Monday set them, Friday review' },
  { id: 'integration', label: 'Integration errors', hint: 'Owners only' },
  { id: 'cold_reply', label: 'Cold email replies', hint: 'Owners: a lead answered a cold email' },
  { id: 'team_request', label: 'Access requests', hint: 'Owners: someone created an account' },
];

export const useNotifications = () =>
  useQuery({
    queryKey: ['notifications'],
    queryFn: () =>
      unwrap<Notice[]>(
        supabase
          .from('notifications')
          .select('id, kind, title, body, url, created_at, read_at')
          .order('created_at', { ascending: false })
          .limit(40),
      ),
  });

export const useNoticePrefs = () =>
  useQuery({
    queryKey: ['notifications', 'prefs'],
    queryFn: () =>
      unwrap<{ kind: string; enabled: boolean }[]>(
        supabase.from('notification_prefs').select('kind, enabled'),
      ),
  });

export async function setNoticePref(kind: string, enabled: boolean) {
  const { error } = await supabase
    .from('notification_prefs')
    .upsert({ kind, enabled }, { onConflict: 'user_id,kind' });
  if (error) throw new Error(error.message);
  await queryClient.invalidateQueries({ queryKey: ['notifications', 'prefs'] });
}

export async function markNoticesRead() {
  await rpc('mark_notifications_read', {});
  await queryClient.invalidateQueries({ queryKey: ['notifications'] });
}

export async function sendTestNotice() {
  await rpc('send_test_notification', {});
  await queryClient.invalidateQueries({ queryKey: ['notifications'] });
}

// Automations (docs/specs/automations.md) -----------------------------------------

export type Automation = {
  id: string;
  key: string;
  name: string;
  trigger_text: string;
  step_labels: string[];
  enabled: boolean;
  dry_run: boolean;
  error_text: string | null;
  definition: { stop_unless_stage?: string[]; start_on_stage?: string[] };
};

export type AutoRun = {
  id: string;
  automation_id: string;
  lead_id: string;
  enrollment_id: string | null;
  step_key: string;
  kind: string;
  status: 'pending' | 'running' | 'sent' | 'skipped' | 'failed' | 'cancelled';
  due_at: string;
  completed_at: string | null;
  result: { would?: string; did?: string; reason?: string; dry_run?: boolean } | null;
  error: string | null;
};

export type Enrollment = {
  id: string;
  automation_id: string;
  lead_id: string;
  status: string;
  enrolled_at: string;
};

export const useAutomations = () =>
  useQuery({
    queryKey: ['automations'],
    queryFn: async () => {
      const since = new Date(Date.now() - 7 * 86_400_000).toISOString();
      const [autos, runs, enrolled, kill] = await Promise.all([
        unwrap<Automation[]>(
          supabase
            .from('automations')
            .select(
              'id, key, name, trigger_text, step_labels, enabled, dry_run, error_text, definition',
            )
            .eq('family', 'internal')
            .order('sort'),
        ),
        unwrap<AutoRun[]>(
          supabase
            .from('scheduled_actions')
            .select(
              'id, automation_id, lead_id, enrollment_id, step_key, kind, status, due_at, completed_at, result, error',
            )
            .or(`completed_at.gte.${since},status.eq.pending`)
            .order('due_at', { ascending: false })
            .limit(500),
        ),
        unwrap<Enrollment[]>(
          supabase
            .from('sequence_enrollments')
            .select('id, automation_id, lead_id, status, enrolled_at')
            .eq('internal', true)
            .eq('status', 'active'),
        ),
        unwrap<{ value: unknown }[]>(
          supabase.from('app_settings').select('value').eq('key', 'kill_switch'),
        ),
      ]);
      return { autos, runs, enrolled, kill: kill[0]?.value === true };
    },
    refetchInterval: 60_000,
  });

export async function setAutomation(key: string, enabled: boolean, dryRun: boolean) {
  await rpc('set_automation', { p_key: key, p_enabled: enabled, p_dry_run: dryRun });
  await queryClient.invalidateQueries({ queryKey: ['automations'] });
}

export async function setKillSwitch(on: boolean) {
  await rpc('set_kill_switch', { p_on: on });
  await queryClient.invalidateQueries({ queryKey: ['automations'] });
}

export async function stopLeadAutomation(enrollmentId: string) {
  await rpc('stop_lead_automation', { p_enrollment: enrollmentId });
  await queryClient.invalidateQueries({ queryKey: ['automations'] });
}

// Cold calling ---------------------------------------------------------------------

export type ColdStats = {
  target: number;
  today: { dials: number; reached: number; conversations: number; booked: number };
  outcomes: { outcome: string; count: number }[];
  hours: { hour: number; dials: number; reached: number }[];
  queue: { ready: number; scheduled: number; done: number };
  callers: { name: string; today: number; total: number }[];
  lists: {
    id: string;
    name: string;
    market: string;
    caller: string | null;
    created_at: string;
    imported: number;
    duplicate: number;
    city_blocked: number;
    bad_phone: number;
    blacklisted: number;
    assigned: number;
    called: number;
    done: number;
    booked: number;
  }[];
  latest: {
    at: string;
    outcome: string;
    note: string | null;
    clinic: string;
    city: string | null;
    caller: string | null;
  }[];
};

export const useColdStats = () =>
  useQuery({
    queryKey: ['coldcall', 'stats'],
    queryFn: () => rpc<ColdStats>('coldcall_stats', {}),
    refetchInterval: 60_000,
  });

export type QueueRow = {
  id: string;
  clinic: string;
  city: string | null;
  locality: string | null;
  market: string;
  phone_e164: string;
  website: string | null;
  email: string | null;
  fb_ads: boolean;
  google_ads: boolean;
  note: string | null;
  attempts: number;
  tag: string | null;
  next_attempt_at: string | null;
  priority: number;
  created_at: string;
};

export type CallHistoryRow = {
  at: string;
  caller: string;
  outcome: string;
  note: string | null;
  callback_at: string | null;
};

export const useCallHistory = (queue: string | undefined) =>
  useQuery({
    queryKey: ['coldcall', 'history', queue],
    queryFn: () => rpc<CallHistoryRow[]>('call_history', { p_queue: queue }),
    enabled: !!queue,
  });

export type PoolRow = {
  id: string;
  clinic: string;
  locality: string | null;
  city: string | null;
  market: string;
  phone_e164: string;
  website: string | null;
  email: string | null;
  fb_ads: boolean;
  google_ads: boolean;
  attempts: number;
  status: 'open' | 'done';
  tag: string | null;
  next_attempt_at: string | null;
  created_at: string;
  last_call_at: string | null;
  held_by: string | null;
  history: { at: string; caller: string; outcome: string; note: string | null }[];
};
export type PoolFilter = 'todo' | 'followup' | 'called' | 'done' | 'all';
export type Pool = { total: number; rows: PoolRow[]; cities: { city: string; n: number }[] };

/** The contact list: search, city, status, newest notes on each row. */
export const usePool = (args: {
  search: string;
  city: string;
  filter: PoolFilter;
  limit: number;
}) =>
  useQuery({
    queryKey: ['coldcall', 'pool', args],
    queryFn: () =>
      rpc<Pool>('call_pool', {
        p_search: args.search || null,
        p_city: args.city || null,
        p_filter: args.filter,
        p_limit: args.limit,
        p_offset: 0,
      }),
    placeholderData: (prev) => prev,
    refetchInterval: 60_000,
  });

export async function claimCall(queue: string) {
  return rpc<{ ok: boolean; reason?: string; by?: string }>('claim_call', { p_queue: queue });
}

export async function releaseCall(queue: string, snooze: boolean) {
  await rpc('release_call', { p_queue: queue, p_snooze: snooze });
  await queryClient.invalidateQueries({ queryKey: ['coldcall'] });
}

export const CALL_OUTCOMES = [
  { id: 'no_answer', label: 'No answer' },
  { id: 'receptionist', label: 'Receptionist' },
  { id: 'wrong_number', label: 'Wrong number' },
  { id: 'not_interested', label: 'Not interested' },
  { id: 'call_back', label: 'Call back' },
  { id: 'send_email', label: 'Send email' },
  { id: 'booked', label: 'Booked' },
] as const;

export type CallOutcomeId = (typeof CALL_OUTCOMES)[number]['id'];

export async function logCall(
  queue: string,
  outcome: CallOutcomeId,
  note: string,
  callbackAt: string | null,
) {
  const r = await rpc<{ lead_id: string | null }>('log_call', {
    p_queue: queue,
    p_outcome: outcome,
    p_note: note || null,
    p_callback_at: callbackAt,
  });
  await queryClient.invalidateQueries({ queryKey: ['coldcall'] });
  if (r.lead_id) for (const key of LEAD_KEYS) void queryClient.invalidateQueries({ queryKey: key });
  return r;
}

export type ImportCounts = {
  total: number;
  imported: number;
  duplicate: number;
  city_blocked: number;
  bad_phone: number;
  blacklisted: number;
  list_id: string | null;
};

export async function importCallRows(args: {
  name: string;
  market: 'TR' | 'UK';
  rows: Record<string, string>[];
  list: string | null;
  caller: string | null;
  dry: boolean;
}) {
  const r = await rpc<ImportCounts>('import_call_list', {
    p_name: args.name,
    p_market: args.market,
    p_rows: args.rows,
    p_list: args.list,
    p_caller: args.caller,
    p_dry: args.dry,
  });
  if (!args.dry) await queryClient.invalidateQueries({ queryKey: ['coldcall'] });
  return r;
}

// Cold email (Instantly mirror) ----------------------------------------------------

export type ColdCampaignRow = {
  id: string;
  name: string;
  market: string | null;
  status: string;
  daily_cap: number;
  sent_today: number;
  sent_total: number;
  replies: number;
  positive: number;
  bounced: number;
  leads_count: number;
  synced_at: string | null;
};
export type ColdInboxRow = {
  id: string;
  email: string;
  daily_limit: number;
  warmup_score: number | null;
  status: 'ok' | 'warn' | 'error';
  note: string | null;
};
export type ColdReplyRow = {
  id: string;
  from_name: string;
  from_email: string | null;
  body: string;
  category: string;
  received_at: string;
  campaign_id: string | null;
  handled_at: string | null;
};
export type ColdDayRow = { day: string; sent: number; replies: number; positive: number };

export const useColdEmail = () =>
  useQuery({
    queryKey: ['coldemail'],
    queryFn: async () => {
      const since = new Date(Date.now() - 14 * 86_400_000).toISOString().slice(0, 10);
      const [campaigns, inboxes, replies, daily] = await Promise.all([
        unwrap<ColdCampaignRow[]>(
          supabase
            .from('cold_campaigns')
            .select(
              'id, name, market, status, daily_cap, sent_today, sent_total, replies, positive, bounced, leads_count, synced_at',
            )
            .order('status')
            .order('sent_total', { ascending: false }),
        ),
        unwrap<ColdInboxRow[]>(
          supabase
            .from('cold_inboxes')
            .select('id, email, daily_limit, warmup_score, status, note')
            .order('email'),
        ),
        unwrap<ColdReplyRow[]>(
          supabase
            .from('cold_replies')
            .select(
              'id, from_name, from_email, body, category, received_at, campaign_id, handled_at',
            )
            .order('received_at', { ascending: false })
            .limit(30),
        ),
        unwrap<ColdDayRow[]>(
          supabase
            .from('cold_daily_stats')
            .select('day, sent, replies, positive')
            .gte('day', since),
        ),
      ]);
      return { campaigns, inboxes, replies, daily };
    },
    refetchInterval: 5 * 60_000,
  });

// WhatsApp (GHL) -----------------------------------------------------------------------

export type WaConversation = {
  id: string;
  lead_id: string | null;
  phone_e164: string;
  display_name: string;
  clinic_label: string | null;
  source: string | null;
  unread_count: number;
  last_message_at: string | null;
  window_expires_at: string | null;
  last_body: string | null;
};
export type WaMessage = {
  id: string;
  direction: 'in' | 'out';
  body: string;
  status: string;
  created_at: string;
  sent_by: string | null;
};

export const useWaConversations = () =>
  useQuery({
    queryKey: ['wa', 'list'],
    queryFn: () =>
      unwrap<WaConversation[]>(
        supabase
          .from('wa_conversations')
          .select(
            'id, lead_id, phone_e164, display_name, clinic_label, source, unread_count, last_message_at, window_expires_at, last_body',
          )
          .order('last_message_at', { ascending: false, nullsFirst: false })
          .limit(300),
      ),
    refetchInterval: 30_000,
  });

export const useWaMessages = (conversation: string | null) =>
  useQuery({
    queryKey: ['wa', 'messages', conversation],
    queryFn: () =>
      unwrap<WaMessage[]>(
        supabase
          .from('messages')
          .select('id, direction, body, status, created_at, sent_by')
          .eq('conversation_id', conversation!)
          .order('created_at', { ascending: true })
          .limit(500),
      ),
    enabled: !!conversation,
    refetchInterval: 15_000,
  });

export async function markWaRead(conversation: string) {
  await supabase.from('wa_conversations').update({ unread_count: 0 }).eq('id', conversation);
  await queryClient.invalidateQueries({ queryKey: ['wa', 'list'] });
}

const WA_ERRORS: Record<string, string> = {
  window_closed:
    'The 24 hour window is closed. Use "Send reminder template" to open the chat again.',
  no_contact: 'This chat has no contact in GoHighLevel yet.',
  unsubscribed: 'This lead unsubscribed. Nothing can be sent to them.',
  not_configured: 'GoHighLevel is not connected yet.',
  window_open: 'The window is open, write the message directly.',
  reopen_recent: 'The reminder template was already requested in the last 24 hours.',
  ghl_scope:
    'GoHighLevel refused the message: the integration may not be allowed to send. Ask Viliam to check the GHL Private Integration scopes.',
};

export async function sendWa(conversation: string, text: string | null, reopen = false) {
  const { error } = await supabase.functions.invoke('wa-send', {
    body: reopen
      ? { conversationId: conversation, reopen: true }
      : { conversationId: conversation, text },
  });
  if (error) {
    let code = '';
    let details = '';
    const ctx = (error as { context?: unknown }).context;
    if (ctx instanceof Response) {
      const j = (await ctx.json().catch(() => ({}))) as { error?: string; details?: string };
      code = j.error ?? '';
      details = j.details ?? '';
    }
    throw new Error(WA_ERRORS[code] ?? (details || 'The message was not sent.'));
  }
  await queryClient.invalidateQueries({ queryKey: ['wa'] });
}

// Offers: one WhatsApp template (and tag) per campaign offer ---------------------------

export type Offer = { key: string; name: string; match: string; wa_tag: string; active: boolean };

export const useOffers = () =>
  useQuery({
    queryKey: ['offers'],
    queryFn: () =>
      unwrap<Offer[]>(
        supabase.from('offers').select('key, name, match, wa_tag, active').order('created_at'),
      ),
  });

export async function saveOffer(o: Offer) {
  await rpc('save_offer', {
    p_key: o.key,
    p_name: o.name,
    p_match: o.match,
    p_tag: o.wa_tag,
    p_active: o.active,
  });
  await queryClient.invalidateQueries({ queryKey: ['offers'] });
}

export const useSaveLeadProfile = () =>
  useRpc((a: { lead: string; profile: Record<string, string | boolean | null> }) =>
    supabase.rpc('save_lead_profile', { p_lead: a.lead, p: a.profile }),
  );
